import { useState, useEffect, useMemo, useRef } from 'react';
import html2canvas from 'html2canvas';
import './index.scss';
import { useUsers } from '@/Recoil/User';
import { useSession } from '@/Recoil/Session/Session';
import { navigateTo } from '@/Utils/routing';
import { useKoreanStudyType } from '@/Recoil/PersonaTest';
import IsometricRoom from '@/Components/IsometricRoom/IsometricRoom';
import Dialog from '@/Components/Dialog';
import { useCharacter, useHomePicture, useOrnament, usePlacement } from '@/Recoil/World';
import ErrorSnackbar from '@/Components/Product/ErrorSnackbar';
import {Delete as DeleteIcon, CameraAlt as CameraIcon} from '@mui/icons-material';
import LoadingCircle from '@/Components/LoadingCircle';
import { logBehavior } from '@/Data/Behavior';

// 성격 유형 데이터
const personalityTypes = {
    'idol_stan': {
        name: 'Si Idol Stan',
        badge: '/img/type_si_idol_stan.svg',
        room: '/img/roomtype_si_idol_stan.jpg',
        descriptions: [
            { emoji: '🩷', text: 'Target cuma satu: biar makin jago ngedukung idol!' },
            { emoji: '🔥', text: 'Ngefans = belajar. Bayangin bias aja udah bisa bikin begadang belajar Korea.' },
            { emoji: '🥹', text: 'Suatu hari pengen bilang "saranghaeyo!" langsung ke bias, makanya belajar terus!' }
        ]
    },
    'k_content_lover': {
        name: 'K-content lover',
        badge: '/img/type_k_content_lover.svg',
        room: '/img/roomtype_k_content_lover.jpg',
        descriptions: [
            { emoji: '🍿', text: 'Belajar hari ini: nonton drama sampai tamat.' },
            { emoji: '👀', text: 'Lirik OST = PR bahasa Korea hari ini.' },
            { emoji: '💥', text: 'Makin ditonton, makin jago. Kamu pemenang sejati dalam belajar Bahasa Korea!' }
        ]
    },
    'social_butterfly': {
        name: 'Social Butterfly',
        badge: '/img/type_social_butterfly.svg',
        room: '/img/roomtype_social_butterfly.jpg',
        descriptions: [
            { emoji: '✨', text: 'Percaya kalau semakin banyak jumlah teman orang Korea, level bahasa Korea juga meningkat' },
            { emoji: '😆', text: 'Salah? Bodo amat—yang penting ngomong dulu!' },
            { emoji: '💬', text: 'Denger "Wah, pengucapan kamu bagus!" dari orang Korea = motivasi naik drastis' }
        ]
    },
    'travel_preparer': {
        name: 'Si Calon Traveler',
        badge: '/img/type_si_calon_traveler.svg',
        room: '/img/roomtype_si_calon_traveler.jpg',
        descriptions: [
            { emoji: '🏝️', text: 'Ngimpi bisa ngobrol lancar pas liburan ke Korea' },
            { emoji: '🇰🇷', text: 'Dari bandara udah siap praktek bahasa Korea beneran.' },
            { emoji: '📍', text: 'Nyasar? Santai. Yang penting bisa nanya pakai bahasa Korea!' }
        ]
    },
    'aesthetic_learner': {
        name: 'Si Paling Estetik',
        badge: '/img/type_si_paling_estetik.svg',
        room: '/img/roomtype_si_paling_estetik.jpg',
        descriptions: [
            { emoji: '🎀', text: 'Begitu coba pakai bahasa Korea, mood langsung jadi lebih emosional' },
            { emoji: '🫧', text: 'Satu stiker, satu kalimat—semua harus Korea banget' },
            { emoji: '📓', text: 'Lagi ngeisi hidupnya pelan-pelan dengan vibes Korea.' }
        ]
    },
    'skill_hunter': {
        name: 'Skill Hunter',
        badge: '/img/type_skill_hunter.svg',
        room: '/img/roomtype_skill_hunter.jpg',
        descriptions: [
            { emoji: '🎯', text: 'Fokus ke level yang lebih tinggi, tiap hari ngasah skill.' },
            { emoji: '📈', text: 'Liat nilai naik tuh rasanya nagih banget.' },
            { emoji: '💡', text: 'Percaya kalau usaha hari ini bisa jadi peluang besar buat besok.' }
        ]
    },
    'academic_learner': {
        name: 'Si Akademis',
        badge: '/img/type_si_akademis.svg',
        room: '/img/roomtype_si_akademis.jpg',
        descriptions: [
            { emoji: '🔍', text: 'Harus paham konsep sampai tuntas biar hati tenang.' },
            { emoji: '🧠', text: 'Kalau grammar dan struktur udah jelas, hidup terasa lebih teratur.' },
            { emoji: '✍️', text: 'Harus ngerti 100% baru bisa lanjut ke step berikutnya.' }
        ]
    },
    'casual_learner': {
        name: 'Casual Learner',
        badge: '/img/type_casual_learner.svg',
        room: '/img/roomtype_casual_learner.jpg',
        descriptions: [
            { emoji: '😎', text: 'Mulai cuma karena penasaran… eh ternyata seru juga belajar Korea.' },
            { emoji: '🕺', text: 'Rajin? Hmm… tergantung mood. Tapi kalau lagi seru, langsung all-in.' },
            { emoji: '🥨', text: 'Tipe pembelajar yang bebas—mood belajar dateng kapan aja tetap nggak canggung.' }
        ]
    }
};

type PersonalityType = keyof typeof personalityTypes;

// 타입별 궁합 데이터
const typeCompatibility: Record<PersonalityType, { goodMatch: PersonalityType; badMatch: PersonalityType }> = {
    'idol_stan':        { goodMatch: 'k_content_lover',   badMatch: 'social_butterfly' },
    'k_content_lover':  { goodMatch: 'idol_stan',         badMatch: 'academic_learner' },
    'social_butterfly': { goodMatch: 'travel_preparer',   badMatch: 'academic_learner' },
    'travel_preparer':  { goodMatch: 'social_butterfly',  badMatch: 'aesthetic_learner' },
    'aesthetic_learner':{ goodMatch: 'k_content_lover',   badMatch: 'academic_learner' },
    'skill_hunter':     { goodMatch: 'academic_learner',  badMatch: 'casual_learner' },
    'academic_learner': { goodMatch: 'skill_hunter',      badMatch: 'casual_learner' },
    'casual_learner':   { goodMatch: 'social_butterfly',  badMatch: 'skill_hunter' },
};

const KoreanStudyTypeFinish = ({path}: {path?: string}) => {
    const {session} = useSession();
    const {profiles, syncProfiles} = useUsers();
    const { results, fetchResult } = useKoreanStudyType();
    const {reset} = useKoreanStudyType();
    const { characters } = useCharacter();
    const { homePicturesByUser, fetchHomePictures, deleteHomePictures } = useHomePicture();
    
    const [askToRestart, setAskToRestart] = useState(false);
    const [askToDeletePhoto, setAskToDeletePhoto] = useState(false);
    
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const {fetchOrnaments} = useOrnament();
    const {fetchPlacements} = usePlacement();


    const character = characters[session.addressid];
    const mainCardRef = useRef<HTMLDivElement>(null);

    const pathParts = path?.split('/').filter(Boolean) ?? [];
    const addressid = decodeURIComponent(pathParts[0] ?? "");

    // Home pictures for this user
    const homePictures = homePicturesByUser[addressid] ?? [];

    useEffect(() => {
        if (addressid) {
            syncProfiles([addressid]);
            fetchResult(addressid);
            fetchHomePictures(addressid);
            fetchPlacements(addressid);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressid]);

    // Fetch catalog and placements
    useEffect(() => {
        fetchOrnaments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const profile = profiles[addressid];
    const result = results[addressid];

    const username = profile?.name;
    const [fabExpanded, setFabExpanded] = useState(false);

    const currentType: PersonalityType = result?.type ?? 'casual_learner';

    const typeData = personalityTypes[currentType];

    // Share handler using Web Share API
    const handleShare = async () => {
        logBehavior('personatest:251212kst:button_press:share_result');
        const shareData = {
            title: `Hasil Tes Kepribadian Bahasa Korea - ${typeData.name}`,
            text: `Kepribadian Bahasa Korea ${username} adalah ${typeData.name}! Coba tes kepribadian bahasa Korea kamu juga!`,
            url: window.location.href,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                // Fallback: copy to clipboard using execCommand for broader support
                const textToCopy = `${shareData.text}\n${shareData.url}`;
                const textArea = document.createElement('textarea');
                textArea.value = textToCopy;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                setSnackbarMessage('Link berhasil disalin!');
                setShowSnackbar(true);
                setTimeout(() => setShowSnackbar(false), 3000);
            }
        } catch (err) {
            // User cancelled or error
            console.log('Share cancelled or failed:', err);
        }
    };

    const handleDownloadCard = async () => {
        if (!mainCardRef.current) { return; }
        logBehavior('personatest:251212kst:button_press:download_card');

        const card = mainCardRef.current;

        // Find and fix the home-picture-image element
        const homePictureImg = card.querySelector('.home-picture-image') as HTMLImageElement | null;
        const originalImgWidth = homePictureImg?.style.width;
        const originalImgHeight = homePictureImg?.style.height;

        const fab = document.querySelector('.fab-container') as HTMLDivElement | null;
        const carouselControls = document.querySelector('.carousel-controls') as HTMLDivElement | null;

        try {
            setIsLoading(true);
            setFabExpanded(false);
            if (fab) {
                fab.style.display = 'none';
            }
            if (carouselControls) {
                carouselControls.style.display = 'none';
            }

            // Wait for FAB to collapse
            await new Promise(resolve => setTimeout(resolve, 350));

            // Set fixed dimensions for home-picture-image to maintain aspect ratio
            if (homePictureImg) {
                const picWidth = document.body.clientWidth - 40 - 20;
                homePictureImg.style.width = `${picWidth / 470 * 520}px`;
                homePictureImg.style.height = `${picWidth / 470 * 520}px`;
            }

            // Wait for reflow
            await new Promise(resolve => setTimeout(resolve, 50));

            const canvas = await html2canvas(card, {
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                scale: 2,
            });

            // Restore original styles
            if (homePictureImg) {
                homePictureImg.style.width = originalImgWidth || '';
                homePictureImg.style.height = originalImgHeight || '';
            }

            const dataUrl = canvas.toDataURL('image/png');
            const link = document.createElement('a');
            link.download = `korean-study-type-${username || 'result'}.png`;
            link.href = dataUrl;
            link.click();

            setSnackbarMessage('Gambar berhasil diunduh!');
            setShowSnackbar(true);
            setTimeout(() => setShowSnackbar(false), 3000);
        } catch (err) {
            console.error('Failed to capture card:', err);
            // Restore original styles on error
            if (homePictureImg) {
                homePictureImg.style.width = originalImgWidth || '';
                homePictureImg.style.height = originalImgHeight || '';
            }
            setSnackbarMessage('Gagal mengunduh gambar.');
            setShowSnackbar(true);
            setTimeout(() => setShowSnackbar(false), 3000);
        } finally {
            if (fab) {
                fab.style.display = 'flex';
            }
            if (carouselControls) {
                carouselControls.style.display = 'flex';
            }
            setIsLoading(false);
        }
    };

    const ButtonsGroup = useMemo(() =>
        addressid === session.addressid ? (
            <div className={`fab-container ${fabExpanded ? 'expanded' : ''}`}>
                {/* Expanded menu items */}
                <div className="fab-menu">
                    {/* Download card button */}
                    <div className={`fab-item ${fabExpanded ? 'show' : ''}`} style={{ transitionDelay: fabExpanded ? '0ms' : '150ms' }}>
                        <span className="fab-tooltip">{"Unduh kartu"}</span>
                        <button className="fab-btn gray" onClick={handleDownloadCard}>
                            <CameraIcon />
                        </button>
                    </div>

                    {/* Invite friends button */}
                    <div className={`fab-item ${fabExpanded ? 'show' : ''}`} style={{ transitionDelay: fabExpanded ? '50ms' : '100ms' }}>
                        <span className="fab-tooltip">{"Undang temanmu ke kamar"}</span>
                        <button className="fab-btn pink" onClick={() => {
                            logBehavior('personatest:251212kst:button_press:makeavatar');
                            navigateTo(`/personatest/koreanstudytype/makeavatar`);
                        }}>
                            <img src="/img/fallow.svg" alt="Follow" />
                        </button>
                    </div>

                    {/* Delete photo button (only if homePictures exist) */}
                    {homePictures.length > 0 && (
                        <div className={`fab-item ${fabExpanded ? 'show' : ''}`} style={{ transitionDelay: fabExpanded ? '100ms' : '50ms' }}>
                            <span className="fab-tooltip">{"Hapus foto kamar ini"}</span>
                            <button className="fab-btn gray" onClick={() => setAskToDeletePhoto(true)}>
                                <DeleteIcon />
                            </button>
                        </div>
                    )}

                    {/* Restart test button */}
                    <div className={`fab-item ${fabExpanded ? 'show' : ''}`} style={{ transitionDelay: fabExpanded ? '150ms' : '0ms' }}>
                        <span className="fab-tooltip">{"Ulangi tes"}</span>
                        <button className="fab-btn gray" onClick={() => setAskToRestart(true)}>
                            <img src="/img/reload.svg" alt="Reload" />
                        </button>
                    </div>
                </div>

                {/* Main FAB button */}
                <button
                    className={`fab-main ${fabExpanded ? 'active' : ''}`}
                    onClick={() => setFabExpanded(!fabExpanded)}
                >
                    <span className="fab-icon">+</span>
                </button>
            </div>
        ) : <></>
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ,[addressid, session.addressid, fabExpanded, homePictures.length]);

    if (!username) {
        return <div className="korean-study-type-finish-container loading-state">
            <p>{"no result: please try again!"}</p>
            <button onClick={() => navigateTo(`/personatest/koreanstudytype`)}>{'Reload'}</button>
        </div>;
    }

    if (!profile) {
        return <div className="korean-study-type-finish-container loading-state">
            <p>{"Loading..."}</p>
        </div>;
    }
    

    return (
        <div className="korean-study-type-finish-container">
                {/* Header Section */}
                <div className="header-section">
                    <p className="subtitle">{`Kepribadian Bahasa Korea ${username} adalah...`}</p>
                </div>

            {/* Main Card */}
            <div className="main-card" ref={mainCardRef}>
                {/* Header Section */}
                <div className="header-section" style={{position: 'absolute', left: "50%", transform: "translateX(-50%)", top: 25, zIndex: 2}}>
                    <div className="type-badge">
                        <div className="badge-icon">
                            <img src={typeData.badge} alt={typeData.name} />
                        </div>
                        <span className="badge-text">{typeData.name}</span>
                    </div>
                </div>
                {homePictures.length > 0 ? (
                    <div className="home-picture-carousel">
                        <img
                            src={homePictures[carouselIndex].image}
                            alt="Home"
                            className="home-picture-image"
                        />
                        {homePictures.length > 1 && (
                            <div className="carousel-controls">
                                <button
                                    className="carousel-btn prev"
                                    onClick={() => setCarouselIndex(i => (i - 1 + homePictures.length) % homePictures.length)}
                                >
                                    ‹
                                </button>
                                <div className="carousel-dots">
                                    {homePictures.map((_, i) => (
                                        <span
                                            key={i}
                                            className={`dot ${i === carouselIndex ? 'active' : ''}`}
                                            onClick={() => setCarouselIndex(i)}
                                        />
                                    ))}
                                </div>
                                <button
                                    className="carousel-btn next"
                                    onClick={() => setCarouselIndex(i => (i + 1) % homePictures.length)}
                                >
                                    ›
                                </button>
                            </div>
                        )}
                        {ButtonsGroup}
                    </div>
                ) : (
                    <div style={{position: 'relative', width: '100%', overflow: 'hidden', }}>
                        <IsometricRoom addressid={addressid} />
                        {character && (
                            <div className="character-preview">
                                <img src={character.image} alt="Character Preview" />
                            </div>
                        )}
                        {ButtonsGroup}
                    </div>
                )}
                <div className="card-logo">
                    <div className="logo-icon">
                        <img src="/img/matelogoguide.png" alt="MATE Logo" />
                    </div>
                    <span className="logo-text">{"MATE"}</span>
                </div>
                    
                {/* Description Card */}
                <div className="description-card">
                    {typeData.descriptions.map((item, index) => (
                        <div className="description-item" key={index}>
                            <span className="emoji">{item.emoji}</span>
                            <p>{item.text}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Compatibility Section */}
            <div className="compatibility-section">
                <h3 className="compatibility-title">{"Tipe yang cocok untuk belajar bareng"}</h3>
                <div className="compatibility-cards">
                    {/* Good Match */}
                    <div className="compatibility-card good">
                        <div className="compatibility-header">
                            <span className="compatibility-emoji">{"💚"}</span>
                            <span className="compatibility-label">{"Cocok banget!"}</span>
                        </div>
                        <div className="compatibility-type">
                            <div className="type-badge-small">
                                <img src={personalityTypes[typeCompatibility[currentType].goodMatch].badge} alt="Good Match" />
                            </div>
                            <span className="type-name">{personalityTypes[typeCompatibility[currentType].goodMatch].name}</span>
                        </div>
                    </div>

                    {/* Bad Match */}
                    <div className="compatibility-card bad">
                        <div className="compatibility-header">
                            <span className="compatibility-emoji">{"💔"}</span>
                            <span className="compatibility-label">{"Belajar sendiri aja"}</span>
                        </div>
                        <div className="compatibility-type">
                            <div className="type-badge-small">
                                <img src={personalityTypes[typeCompatibility[currentType].badMatch].badge} alt="Bad Match" />
                            </div>
                            <span className="type-name">{personalityTypes[typeCompatibility[currentType].badMatch].name}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Feature Cards */}
            <div className="feature-cards">
                {/* Room Decoration Card */}
                <div className="feature-card pink">
                    <h3 className="feature-title">
                        {`${username}, mau dekor kamar kamu lebih lanjut?
                        |Kami akan memasukkan kamar ini ke dalam aplikasi Mate.`.replace(/\n *\|/g, '\n')}
                    </h3>
                    <div className="feature-image ornament">
                        <div className="ornament-track">
                            <img src="/img/onarments_rolling.jpg" alt="Room Ornaments" />
                            <img src="/img/onarments_rolling.jpg" alt="Room Ornaments" />
                        </div>
                    </div>
                    <p className="feature-description">
                        {`Semakin sering kamu belajar di Mate,
                        |semakin keren kamu bisa dekor ruanganmu!`.replace(/\n *\|/g, '\n')}
                    </p>
                </div>

                {/* Coupon Card */}
                <div className="feature-card pink">
                    <h3 className="feature-title">
                        {`Hadiah 30 koin yang bisa digunakan
                        |di aplikasi Mate untuk kamu.`.replace(/\n *\|/g, '\n')}
                    </h3>
                    <div className="feature-image coupon">
                        <img src="/img/coupon.jpg" alt="30 Koin Coupon" />
                    </div>
                    <p className="feature-highlight">{"Semakin bagus kemampuan bahasa Koreamu, semakin bagus juga kamarmu!"}</p>
                </div>
            </div>

            {/* CTA Section */}
            <div className="cta-section">
                {addressid === session.addressid ? <div className="cta-wrapper">
                    <button className="btn-share" onClick={handleShare}>{"Ajak temanmu dan bandingkan hasilnya"}</button>
                    <button className="btn-continue" onClick={() => {
                        logBehavior('personatest:251212kst:button_press:continue_decorating');
                        navigateTo('https://koreanmate.onelink.me/Tkco/113z0nwu')
                    }}>{"Lanjutkan dekor kamar"}</button>
                </div> : <div className="cta-wrapper">
                    <button className="btn-share" onClick={() => {
                        logBehavior('personatest:251212kst:button_press:take_own_test', {referral: addressid});
                        navigateTo(`/personatest/koreanstudytype`)
                    }}>{"Coba tes kepribadian bahasa Koreamu sendiri"}</button>
                </div>}
            </div>

            {/* Footer */}
            <div className="footer-section">
                <div className="footer-left">
                    <span className="footer-logo">{"MATE"}</span>
                    <span className="footer-subtitle">{"Talk&Learn Korean"}</span>
                </div>
                <div className="footer-icon">
                    <img src="/img/matelogo_gray.png" alt="MATE Logo" />
                </div>
            </div>
            <LoadingCircle show={isLoading} />
            <Dialog visible={askToRestart} onDismiss={() => setAskToRestart(false)}>
                <div className="DialogContent">
                    <p className="Text DialogHeadline">{"Apakah kamu yakin ingin mengulang tes?"}</p>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                        <button className="DialogButton Pressable DialogSecondaryButton" onClick={() => setAskToRestart(false)}>{'Batal'}</button>
                        <button className="DialogButton Pressable DialogPrimaryButton" onClick={async () => {
                            reset();
                            logBehavior('personatest:251212kst:button_press:restart');
                            navigateTo(`/personatest/koreanstudytype`);
                        }}>{'Ya, ulangi tes'}</button>
                    </div>
                </div>
            </Dialog>
            
            <Dialog visible={askToDeletePhoto} onDismiss={() => setAskToDeletePhoto(false)}>
                <div className="DialogContent">
                    <p className="Text DialogHeadline">{"Apakah kamu yakin ingin menghapus foto kamar ini?"}</p>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                        <button className="DialogButton Pressable DialogSecondaryButton" onClick={() => setAskToDeletePhoto(false)}>{'Batal'}</button>
                        <button className="DialogButton Pressable DialogPrimaryButton" onClick={async () => {
                            setIsLoading(true);
                            logBehavior('personatest:251212kst:button_press:delete_home_picture', {picture: homePictures[carouselIndex].image});
                            await deleteHomePictures([homePictures[carouselIndex].image])
                                .catch(()=>{
                                    setIsLoading(false);
                                    setAskToDeletePhoto(false);
                                });
                            setIsLoading(false);
                            setAskToDeletePhoto(false);
                        }}>{'Ya, ulangi tes'}</button>
                    </div>
                </div>
            </Dialog>
            <ErrorSnackbar show={showSnackbar} message={snackbarMessage} />
        </div>
    );
};

export default KoreanStudyTypeFinish;
